// packages import
import { yupResolver } from '@hookform/resolvers/yup'
import { Button, CircularProgress, DialogActions, FormControl, Grid, TextField } from '@mui/material'
import { FC, useEffect, useState } from 'react'
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form'

// component import
import { Alert } from '../../common/Alert'
import { CustomController } from '../../common/CustomController'
import { CustomModal } from '../../common/CustomModal'
import MatterDropdownWithForm from '../../common/MatterDropdownWithForm'

// others
import {
  Case,
  PaymentMethod,
  PaymentSource,
  useFetchPaymentByIdLazyQuery,
  useUpdatePaymentMutation
} from '../../../../generated'
import { BAD_USER_INPUT, CANCEL, GRAPHQL_QUERY_POLICY, SOMETHING_WENT_WRONG } from '../../../constants'
import AccountDropdown from '../../common/AccountDropdown'
import PaymentMethodController from '../../common/PaymentMethodController'
import OfficeAutoComplete from '../../offices/OfficeAutocomplete'
import { CustomDatePickerController } from '../../common/CustomDatePickerRController'
import { formatCurrency } from '../../../utils'

const availablePaymentMethods = [PaymentMethod.Cash, PaymentMethod.Cheque, PaymentMethod.Card, PaymentMethod.Other]

export interface EditPaymentModalProps {
  paymentId: string
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  paymentMethods?: PaymentMethod[]
  /**
   * Wording overrides so the same form can edit a refund, whose amount, bank account and matter are
   * every bit as locked as a payment's but where "payment" is the wrong word for what is on screen.
   */
  title?: string
  submitLabel?: string
  successMessage?: string
  accountLabel?: string
}

export interface EditPaymentFormValues {
  date: string
  paymentMethod: PaymentMethod
  referenceNo: string
  slipNumber: string
  description: string
  destinationAccount: {
    value: string
    label: string
  } | null
  location: {
    value: number
    label: string
  } | null
  matter: string | null
}

export const EditPaymentModal: FC<EditPaymentModalProps> = ({
  paymentId,
  isOpen,
  onClose,
  onSuccess,
  paymentMethods,
  title = 'Edit Payment',
  submitLabel = 'Update Payment',
  successMessage = 'Payment updated successfully',
  accountLabel = 'Destination Account'
}): JSX.Element => {
  const [paymentAmount, setPaymentAmount] = useState<number>(0)
  const [initialCase, setInitialCase] = useState<Partial<Case> | null>(null)
  const [isFirmPayment, setIsFirmPayment] = useState<boolean>(false)

  const methods = useForm<EditPaymentFormValues>({
    mode: 'all',
    defaultValues: {
      date: new Date().toISOString().split('T')[0],
      paymentMethod: PaymentMethod.Cheque,
      referenceNo: '',
      slipNumber: '',
      description: '',
      destinationAccount: null,
      location: null,
      matter: null
    }
  })

  const {
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors }
  } = methods

  const [fetchPaymentById, { loading: loadingPayment }] = useFetchPaymentByIdLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError(error) {
      Alert.error(error.message)
    },
    onCompleted(data) {
      const { fetchPaymentById } = data || {}
      const { payment } = fetchPaymentById || {}
      if (payment) {
        setPaymentAmount(payment.amount)
        setIsFirmPayment(payment.paymentSource === PaymentSource.FirmPayment)
        setInitialCase(payment.case ? (payment.case as Partial<Case>) : null)
        reset({
          date: new Date(payment.date).toISOString().split('T')[0],
          paymentMethod: payment.paymentMethod,
          referenceNo: payment.referenceNumber || '',
          slipNumber: payment.slipNumber || '',
          description: payment.notes || '',
          destinationAccount: payment.account
            ? {
                value: payment.account.id,
                label: payment.account.name || ''
              }
            : null,
          location: payment.office
            ? {
                value: payment.office.id,
                label: payment.office.name || ''
              }
            : null,
          matter: payment.case?.id ?? null
        })
      }
    }
  })

  const [updatePaymentMutation, { loading }] = useUpdatePaymentMutation({
    onError(error) {
      const { graphQLErrors } = error

      if (graphQLErrors) {
        graphQLErrors.map(({ extensions }) => {
          if (extensions) {
            const { code } = extensions
            if (code === BAD_USER_INPUT) {
              Alert.error(error.message)
            } else {
              Alert.error(SOMETHING_WENT_WRONG)
            }
          }

          return null
        })
      } else {
        Alert.error(SOMETHING_WENT_WRONG)
      }
    },
    onCompleted(data) {
      if (data) {
        const {
          updatePayment: { response }
        } = data

        if (response) {
          const { status } = response

          if (status === 200) {
            Alert.success(successMessage)
            onClose()
            onSuccess()
          }
        }
      }
    }
  })

  const onSubmit: SubmitHandler<EditPaymentFormValues> = async data => {
    const { description, paymentMethod, referenceNo, slipNumber, destinationAccount, location, matter } = data

    await updatePaymentMutation({
      variables: {
        updatePaymentInput: {
          id: paymentId,
          paymentMethod,
          referenceNumber: referenceNo,
          slipNumber: slipNumber || null,
          notes: description,
          destinationAccountId: destinationAccount?.value,
          locationId: location?.value,
          // The Matter field is only rendered for firm payments, and the API rejects `caseId` on any
          // other payment source. Sending the unchanged value regardless made every edit of a payment
          // attached to a matter fail with "Matter can only be changed on firm payments".
          ...(isFirmPayment && { caseId: matter || null })
        } as any
      }
    })
  }

  const handleClose = () => {
    onClose()
  }

  useEffect(() => {
    if (isOpen && paymentId) {
      fetchPaymentById({
        variables: {
          paymentId
        }
      })
    }
  }, [isOpen, paymentId, fetchPaymentById])

  return (
    <CustomModal title={title} isOpen={isOpen} handleClose={handleClose} maxWidth="md">
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid container item xs={12} spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Amount"
                  value={formatCurrency(paymentAmount)}
                  disabled
                  fullWidth
                  helperText="Amount cannot be edited"
                  sx={{ my: 0 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <CustomDatePickerController
                  isDisabled={true}
                  controllerName="date"
                  controllerLabel="Date"
                  sx={{ my: 0 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <PaymentMethodController
                  name="paymentMethod"
                  label="Payment By"
                  paymentMethods={paymentMethods ?? availablePaymentMethods}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <CustomController
                  controllerName="referenceNo"
                  controllerLabel="Reference No."
                  fieldType="text"
                  sx={{ my: 0 }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl variant="outlined" fullWidth>
                  <Controller
                    disabled={true}
                    name="destinationAccount"
                    control={control}
                    render={({ field }) => (
                      <AccountDropdown
                        disabled={true}
                        value={field.value}
                        onChange={field.onChange}
                        searchFilters={{
                          isBankAccount: true
                        }}
                        errorMessage={errors.destinationAccount?.message || errors.destinationAccount?.value?.message}
                        label={accountLabel}
                      />
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <OfficeAutoComplete name="location" label="Location of Payment" autoSelectOnTab />
              </Grid>

              <Grid item xs={12} md={6}>
                <CustomController controllerName="slipNumber" controllerLabel="Slip No." fieldType="text" />
              </Grid>

              {isFirmPayment && (
                <Grid item xs={12}>
                  <MatterDropdownWithForm name="matter" label="Matter" initialCase={initialCase as Case} userId={''} />
                </Grid>
              )}

              <Grid item xs={12}>
                <CustomController
                  controllerName="description"
                  controllerLabel="Notes"
                  fieldType="textarea"
                  isMultiLine
                  rowsLength={4}
                  sx={{ my: 0 }}
                />
              </Grid>
            </Grid>
          </Grid>

          <DialogActions sx={{ padding: '30px 0 0' }}>
            <Button onClick={handleClose}>{CANCEL}</Button>

            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={loading || loadingPayment}
              endIcon={(loading || loadingPayment) && <CircularProgress size={20} color="inherit" />}
            >
              {submitLabel}
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </CustomModal>
  )
}

export default EditPaymentModal
